import { useFloating, flip, shift, autoUpdate, inline, offset } from '@floating-ui/react-dom'
import { cloneElement, forwardRef, PropsWithChildren, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Styles from './Tooltip.module.scss'
import { PopoverProps, TooltipProps } from './Types'
import cx from 'classnames'
import { TooltipTriggerState } from '@react-stately/tooltip'
import { useTooltipTrigger, useTooltip } from '@react-aria/tooltip'
import { mergeProps, useObjectRef } from '@react-aria/utils'
import { CSSTransition } from 'react-transition-group'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'

interface CustomTooltipTriggerState extends TooltipTriggerState {
  onTouchStart: () => void
  onTouchEnd: () => void
}

// Similar to 'useTooltipTriggerState' from '@react-stately/tooltip', but instead of a global timeout it uses a local one.
// Therefore tooltips always require a delay, even if another tooltip was visible when the mouse enters a new one.
const useTooltipTriggerStateCustom = (delayMs: number = 500, closeDelayMs: number = 0): CustomTooltipTriggerState => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const openTimeout = useRef<NodeJS.Timeout | null>(null)
  const closeTimeout = useRef<NodeJS.Timeout | null>(null)

  const clearAllTimeouts = () => {
    if (openTimeout.current) {
      clearTimeout(openTimeout.current)
      openTimeout.current = null
    }
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current)
      closeTimeout.current = null
    }
  }

  return {
    isOpen,
    close: () => {
      clearAllTimeouts()

      if (closeDelayMs > 0) {
        closeTimeout.current = setTimeout(() => setIsOpen(false), closeDelayMs)
      } else {
        setIsOpen(false)
      }
    },
    open: (immediate?: boolean) => {
      clearAllTimeouts()

      if (immediate) {
        setIsOpen(true)
      } else {
        openTimeout.current = setTimeout(() => setIsOpen(true), delayMs)
      }
    },
    onTouchStart: () => {
      clearAllTimeouts()
      setIsOpen(true)
    },
    onTouchEnd: () => {
      clearAllTimeouts()
      setIsOpen(false)
    },
  }
}

const Popover = forwardRef<HTMLDivElement, PopoverProps>(({ state, style, ...props }, ref) => {
  const { tooltipProps } = useTooltip(props, state)

  return (
    <FloatingPortal id="floating-ui-root">
      <CSSTransition
        in={state.isOpen}
        classNames={{
          enter: Styles.enter,
          enterActive: Styles.enterActive,
          exit: Styles.exit,
          exitActive: Styles.exitActive,
        }}
        timeout={100}
        unmountOnExit
      >
        <div className={cx(Styles.tooltip, props.className)} ref={ref} role="tooltip" style={style} {...tooltipProps}>
          {props.label}
        </div>
      </CSSTransition>
    </FloatingPortal>
  )
})

export const Tooltip = forwardRef<HTMLElement, PropsWithChildren<TooltipProps>>(
  (
    {
      children,
      label,
      placement = 'top',
      className,
      strategy = 'fixed',
      tooltipState,
      hide,
      mouseEnterDelay,
      mouseLeaveDelay,
    },
    fRef,
  ) => {
    const ref = useObjectRef(fRef)

    const {
      x,
      y,
      reference,
      floating,
      strategy: floatingStrategy,
    } = useFloating({
      placement,
      strategy,
      middleware: [inline(), flip(), shift({ padding: 4 }), offset(4)],
      whileElementsMounted: (reference, floating, update) => {
        return autoUpdate(reference, floating, update, {
          animationFrame: true,
        })
      },
    })

    const state = useTooltipTriggerStateCustom(mouseEnterDelay, mouseLeaveDelay)
    const { triggerProps, tooltipProps } = useTooltipTrigger({}, state, ref)

    useLayoutEffect(() => {
      reference(ref.current)
    }, [ref.current]) // eslint-disable-line react-hooks/exhaustive-deps

    const combinedRef = useMemo(
      () => (el: any) => {
        ref.current = el
        if ((children as any).ref) {
          ;(children as any).ref.current = el
        }
      },
      [ref, children],
    )

    if (hide) return <>{children}</>

    return (
      <>
        {cloneElement(children, {
          ...mergeProps(children.props, triggerProps, {
            onTouchStart: state.onTouchStart,
            onTouchEnd: state.onTouchEnd,
          }),
          ref: combinedRef,
        })}
        <Popover
          state={{ ...state, ...tooltipState }}
          ref={floating}
          label={label}
          className={className}
          style={{
            position: floatingStrategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
          {...tooltipProps}
        />
      </>
    )
  },
)
